// ============================================
// FILE: /frontend/src/api/finance.api.js
// PURPOSE: Finance application API client functions
// ============================================

import apiClient from './client.js';

console.log('[FinanceAPI] 🚀 Initializing Finance API client');

// ========================================
// PUBLIC API FUNCTIONS
// ========================================

/**
 * Submit a new finance application
 * @param {Object} applicationData - Finance application data
 * @returns {Promise} - API response
 */
export const submitFinanceApplication = async (applicationData) => {
  console.log('[FinanceAPI] 📝 Submitting finance application');
  console.log('[FinanceAPI] Data:', applicationData);

  try {
    const response = await apiClient.post('/finance/applications', applicationData);
    console.log('[FinanceAPI] ✅ Application submitted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('[FinanceAPI] ❌ Error submitting application:', error);
    throw error;
  }
};

/**
 * Download application PDF
 * @param {string} applicationId - Application ID
 * @returns {Promise<Blob>} - PDF blob
 */
export const downloadApplicationPDF = async (applicationId) => {
  console.log('[FinanceAPI] 📄 Downloading PDF for application:', applicationId);

  try {
    const response = await apiClient.get(`/finance/applications/${applicationId}/pdf`, {
      responseType: 'blob'
    });
    console.log('[FinanceAPI] ✅ PDF downloaded successfully');
    return response.data;
  } catch (error) {
    console.error('[FinanceAPI] ❌ Error downloading PDF:', error);
    throw error;
  }
};

// ========================================
// ADMIN API FUNCTIONS
// ========================================

/**
 * Get all finance applications (Admin)
 * @param {Object} params - Query parameters (status, financeType, page, limit)
 * @returns {Promise} - API response
 */
export const getAllApplications = async (params = {}) => {
  console.log('[FinanceAPI] 📋 Fetching all applications');
  console.log('[FinanceAPI] Params:', params);

  try {
    const response = await apiClient.get('/finance/applications', { params });
    console.log('[FinanceAPI] ✅ Applications fetched:', response.data.data.length);
    return response.data;
  } catch (error) {
    console.error('[FinanceAPI] ❌ Error fetching applications:', error);
    throw error;
  }
};

/**
 * Get single application by ID (Admin)
 * @param {string} applicationId - Application ID
 * @returns {Promise} - API response
 */
export const getApplicationById = async (applicationId) => {
  console.log('[FinanceAPI] 🔍 Fetching application:', applicationId);

  try {
    const response = await apiClient.get(`/finance/applications/${applicationId}`);
    console.log('[FinanceAPI] ✅ Application fetched');
    return response.data;
  } catch (error) {
    console.error('[FinanceAPI] ❌ Error fetching application:', error);
    throw error;
  }
};

/**
 * Update application status (Admin)
 * @param {string} applicationId - Application ID
 * @param {string} status - New status (PENDING, APPROVED, REJECTED)
 * @param {string} adminNotes - Optional admin notes
 * @returns {Promise} - API response
 */
export const updateApplicationStatus = async (applicationId, status, adminNotes = '') => {
  console.log('[FinanceAPI] 🔄 Updating application status');
  console.log('[FinanceAPI] ID:', applicationId, 'Status:', status);

  try {
    const response = await apiClient.patch(`/finance/applications/${applicationId}/status`, {
      status,
      adminNotes
    });
    console.log('[FinanceAPI] ✅ Status updated successfully');
    return response.data;
  } catch (error) {
    console.error('[FinanceAPI] ❌ Error updating status:', error);
    throw error;
  }
};

/**
 * Get application statistics (Admin)
 * @returns {Promise} - API response with stats
 */
export const getApplicationStats = async () => {
  console.log('[FinanceAPI] 📊 Fetching application statistics');

  try {
    const response = await apiClient.get('/finance/stats');
    console.log('[FinanceAPI] ✅ Statistics fetched:', response.data);
    return response.data;
  } catch (error) {
    console.error('[FinanceAPI] ❌ Error fetching statistics:', error);
    throw error;
  }
};

console.log('[FinanceAPI] ✅ Finance API client initialized successfully');